// src/data/requests.ts
// ---------------------
// Repository for the `requests` table + its `requests_view` read view — the first
// module of the src/data/ layer (architecture-review item 3.2). It owns the
// table/view names, the read column list, and (incrementally) the request RPC
// wrappers, so a schema change touches this file instead of ~12 call sites. It
// also owns the row → app-object mapping (dbToReq), so callers get domain objects
// and never see PostgREST shapes.
//
// Typed via getTypedClient() against the generated Database schema, so the query
// construction here is schema-checked (this is what catches "column doesn't exist
// on this relation" bugs). Functions log on error and return null (caller treats
// null as "didn't load — leave state as-is"); no client yet → null too.
//
// Row → domain seam: getTypedClient() yields the generated row types, whose jsonb
// columns are `Json` and whose nullable columns use `| null`. dbToReq is written
// against a looser hand-shaped row; bridging the two precisely is a later
// enrichment (see docs/data-layer-plan.md), so we apply ONE localised cast at the
// map boundary here rather than pushing casts through every caller.

import type { Database, Json } from "../types/database.generated";
import { ACTIVE_REQUEST_STATUSES } from "../utils/enums";
import { dbToReq } from "../utils/helpers";
import { logger } from "../utils/logger";
import { toNullableEmail } from "../utils/sanitize";
import { getTypedClient } from "../utils/supabase";
import { cleanEmailList } from "./emailFields";

// The app-request object every caller works with (snake→camel, status re-derived).
export type AppRequest = ReturnType<typeof dbToReq>;
// dbToReq's (loose, hand-shaped) input row — the target of the boundary cast.
type DbViewRow = Parameters<typeof dbToReq>[0];

// Columns the app reads from requests_view (moved here from usePortalData).
// Typed as `string` (not a string literal) on purpose: a literal would make
// supabase-js try to parse 28 columns into a result type, which is slow/deep for
// tsc. With `string`, .select() returns the full view Row, which is all we need.
// NOTE (identity Pass 2): `shelter_code` here is the LINKED shelter's attribute,
// surfaced by requests_view's shelter_id join (db-85) exactly like shelter_name —
// it is a display value, NOT a request column and never a query/join key. The
// requests table links to its shelter only by shelter_id.
//
// ⚠️ LIVE SIDE-READS (register — John, 31 Jul 2026): a few service attributes are
// displayed on a request WITHOUT being carried by requests_view; the UI fetches
// them live from the `shelters` table on that one screen instead (a deliberate
// compromise — avoids a view migration for a single field, and stays retroactive).
// If you EXTEND requests_view's shelters join, prefer folding these in here + on
// requests_view, then retire the side-read (they don't scale to list views — one
// extra query per row). Known side-reads today:
//   • customer_type / its option_category (db-124) — StaffTopInfoStrip's category
//     chip, via getShelterCustomerType() + getOptionCategory().
// Add to this list whenever a new live side-read is introduced.
export const REQUEST_COLS: string =
  "id,ref_no,shelter_id,shelter_ref,shelter_name,shelter_code,state,status,date,delivery_address_id,delivery_address_override,delivery_address_resolved,phone,mobile,caseworker_note,contact_name,shelter_address,non_registered_address,notes,children,invoices,docs,updated_at,updated_by,updated_by_role,cc_emails,shelter_email,created_at,approvals_confirmed_at,delivery_address_verified,delivery_address_banned,request_type";

const ORDER_COL = "created_at";
// Exported so screens that EXPORT this list (Reports) can tell the user when they
// are looking at a capped set rather than everything. Do NOT raise it as a
// shortcut — the cap is what keeps the RLS-heavy list query inside the
// `authenticated` statement timeout (docs/rls-perf-plan.md).
export const LIST_LIMIT = 500;

function toAppRequests(rows: unknown[] | null): AppRequest[] {
  return (rows ?? []).map((r) => dbToReq(r as unknown as DbViewRow));
}

// All requests the current user may see (RLS-scoped). Used by staff / admin /
// responder-mode, and as the shelter fallback when there is no resolved shelter id.
export async function listVisibleRequests(): Promise<AppRequest[] | null> {
  const c = getTypedClient();
  if (!c) return null;
  const { data, error } = await c
    .from("requests_view")
    .select(REQUEST_COLS)
    .order(ORDER_COL, { ascending: false })
    .limit(LIST_LIMIT);
  if (error) {
    logger.warn("listVisibleRequests failed:", error.message);
    return null;
  }
  return toAppRequests(data);
}

// A shelter's requests, filtered by shelter_id — the per-request FK to the ONE
// shelter that submitted the request (shelters.id). NOT the auth user's uuid: that
// older meaning was retired when the key moved to the surrogate shelter FK (db-14),
// and shelter_id has been NOT NULL since db-82. The caller passes the session's
// shelterUuid, which tracks the user's ACTIVE shelter 1:1 with the (immutable, db-75)
// code — so a multi-shelter user is scoped to exactly the shelter they're acting for,
// with no cross-contamination. (Identity Phase 2 — replaces the old by-code filter.)
export async function listRequestsByShelterId(shelterId: string): Promise<AppRequest[] | null> {
  const c = getTypedClient();
  if (!c) return null;
  const { data, error } = await c
    .from("requests_view")
    .select(REQUEST_COLS)
    .eq("shelter_id", shelterId)
    .order(ORDER_COL, { ascending: false })
    .limit(LIST_LIMIT);
  if (error) {
    logger.warn("listRequestsByShelterId failed:", error.message);
    return null;
  }
  return toAppRequests(data);
}

// Safety stop for listAllVisibleRequests: 200 pages × LIST_LIMIT = 100,000 rows.
export const ALL_REQUESTS_MAX_PAGES = 200;

// EVERY request the current user may see — not capped at LIST_LIMIT (12 Oct 2026).
// Fetched in pages of LIST_LIMIT, so each query stays as small as the capped list's
// and inside the statement timeout. Oldest first (created_at, then id), so a request
// created mid-fetch lands on the last page and never shifts an earlier one; rows are
// de-duplicated by id and returned newest first, like listVisibleRequests.
// FOR THE STATISTICS SCREEN + STANDARD REPORTS ONLY, fetched when they are opened /
// printed. Do NOT use it for the shared app-wide list: that one reloads on every
// Realtime change, which is exactly why it stays capped.
// Returns null if any page fails (a partial history would look complete).
export async function listAllVisibleRequests(): Promise<AppRequest[] | null> {
  const c = getTypedClient();
  if (!c) return null;
  const rows: unknown[] = [];
  for (let page = 0; page < ALL_REQUESTS_MAX_PAGES; page++) {
    const start = page * LIST_LIMIT;
    const { data, error } = await c
      .from("requests_view")
      .select(REQUEST_COLS)
      .order(ORDER_COL, { ascending: true })
      .order("id", { ascending: true })
      .range(start, start + LIST_LIMIT - 1);
    if (error) {
      logger.warn("listAllVisibleRequests failed:", error.message);
      return null;
    }
    const batch = (data ?? []) as unknown[];
    rows.push(...batch);
    if (batch.length < LIST_LIMIT) break;
    if (page === ALL_REQUESTS_MAX_PAGES - 1) {
      logger.warn("listAllVisibleRequests hit ALL_REQUESTS_MAX_PAGES; results truncated");
    }
  }
  const seen = new Set<unknown>();
  const unique: unknown[] = [];
  for (let i = rows.length - 1; i >= 0; i--) {
    const id = (rows[i] as { id?: unknown }).id;
    if (seen.has(id)) continue;
    seen.add(id);
    unique.push(rows[i]);
  }
  return toAppRequests(unique);
}

// ── Writes + post-write helpers (the save/submit path) ───────────────────────
// reqToDb builds a loose db-row object; cast to the generated Update at the
// boundary. Each logs on error. The runtime client is the same instance the App
// holds (getTypedClient), so RLS behaviour is unchanged from the prior inline calls.

type RequestInsert = Database["public"]["Tables"]["requests"]["Insert"];
type RequestUpdate = Database["public"]["Tables"]["requests"]["Update"];
type RequestRow = Database["public"]["Tables"]["requests"]["Row"];
type WriteRow = object;
type WriteResult = { error: { message: string } | null };
type ReturningResult = { data: RequestRow | null; error: { message: string } | null };
// `updated:false` (with no error) means the optimistic-concurrency guard didn't match —
// i.e. someone else confirmed since this editor loaded; the caller rejects rather than overwrites.
type ConflictableResult = { updated: boolean; error: { message: string } | null };

// Email snapshot columns on a request (email-validation-plan §6B), normalised on every
// write: shelter_email trimmed + lowercased (blank → NULL); cc_emails normalised,
// de-duplicated, with blank/unsendable entries dropped. LENIENT on purpose — these are
// snapshots copied from the submitter's login and the ticked colleague list (never typed
// here), so a bad value must never block a DFV Service from submitting a request.
// Only keys PRESENT on the row are touched.
export function cleanRequestEmails<T extends object>(row: T): T {
  if (!row || typeof row !== "object") return row;
  const src = row as Record<string, unknown>;
  const out: Record<string, unknown> = { ...src };
  if (src.shelter_email !== undefined) out.shelter_email = toNullableEmail(src.shelter_email);
  if (src.cc_emails !== undefined && src.cc_emails !== null) {
    out.cc_emails = cleanEmailList(src.cc_emails);
  }
  return out as T;
}

// Insert a new request row and return it (.select().single()). This is the
// RLS-critical create path (the shape of the historical Phase-2 7-day bug); the
// runtime client is the same instance as before, so the WITH CHECK policies that
// guard shelter inserts behave identically.
export async function insertRequest(row: WriteRow): Promise<ReturningResult> {
  const c = getTypedClient();
  if (!c) return { data: null, error: null };
  const { data, error } = await c
    .from("requests")
    .insert(cleanRequestEmails(row) as RequestInsert)
    .select()
    .single();
  if (error) logger.warn("insertRequest failed:", error.message);
  return { data: (data as RequestRow) ?? null, error };
}

// Update a request row by id (no return) — the general / staff inline save.
export async function updateRequestById(
  id: string | number | null | undefined,
  row: WriteRow
): Promise<WriteResult> {
  const c = getTypedClient();
  if (!c) return { error: null };
  const { error } = await c
    .from("requests")
    .update(cleanRequestEmails(row) as RequestUpdate)
    .eq("id", id as number);
  if (error) logger.warn("updateRequestById failed:", error.message);
  return { error };
}

// Optimistic-concurrency ("compare-and-swap") update for the approval Confirm & Send
// path. Updates the row ONLY if its persisted approvals_confirmed_at still equals the
// baseline the editor opened with — i.e. nobody else has confirmed since. Returns
// { updated: false } (no error) when the guard didn't match, so the caller can reject the
// save (and tell the user to reload + re-review) instead of silently overwriting another
// tab/user/device's confirmation. approvals_confirmed_at is the right token: Confirm & Send
// is the only writer of it and stamps a fresh timestamp each time, so any change since load
// is a competing confirm. NULL is matched with `.is(null)` (the unconfirmed baseline).
export async function updateRequestByIdIfConfirmedAtMatches(
  id: string | number | null | undefined,
  expectedConfirmedAt: string | null,
  row: WriteRow
): Promise<ConflictableResult> {
  const c = getTypedClient();
  if (!c) return { updated: false, error: null };
  const base = c
    .from("requests")
    .update(cleanRequestEmails(row) as RequestUpdate)
    .eq("id", id as number);
  const guarded =
    expectedConfirmedAt == null
      ? base.is("approvals_confirmed_at", null)
      : base.eq("approvals_confirmed_at", expectedConfirmedAt);
  const { data, error } = await guarded.select("id");
  if (error) logger.warn("updateRequestByIdIfConfirmedAtMatches failed:", error.message);
  return { updated: !error && Array.isArray(data) && data.length > 0, error };
}

// Update a request row by id and return the updated row (.select().single()) —
// the draft save/edit paths that re-hydrate afterwards.
export async function updateRequestByIdReturning(
  id: string | number | undefined,
  row: WriteRow
): Promise<ReturningResult> {
  const c = getTypedClient();
  if (!c) return { data: null, error: null };
  const { data, error } = await c
    .from("requests")
    .update(cleanRequestEmails(row) as RequestUpdate)
    .eq("id", id as number)
    .select()
    .single();
  if (error) logger.warn("updateRequestByIdReturning failed:", error.message);
  return { data: (data as RequestRow) ?? null, error };
}

// Re-hydrate a single request from the read view after a write, so dbToReq sees
// the children/joined columns. Returns the raw view row (caller maps via dbToReq),
// or null on error / not found / no client.
export async function getRequestViewRowById(
  id: string | number | undefined
): Promise<DbViewRow | null> {
  const c = getTypedClient();
  if (!c) return null;
  const { data, error } = await c
    .from("requests_view")
    .select("*")
    .eq("id", id as number)
    .single();
  if (error) {
    logger.warn("getRequestViewRowById failed:", error.message);
    return null;
  }
  return (data as unknown as DbViewRow) ?? null;
}

// Fresh docs + invoices arrays for a request — the concurrency-safe re-read the
// file-upload flows do before writing, so a concurrent uploader's files aren't
// clobbered. Returns loose arrays (the caller owns the DocEntry/url element types).
export type RequestFiles = { docs: unknown[] | null; invoices: unknown[] | null };
export async function getRequestFilesById(
  id: string | number | null | undefined
): Promise<RequestFiles | null> {
  const c = getTypedClient();
  if (!c) return null;
  const { data, error } = await c
    .from("requests")
    .select("docs,invoices")
    .eq("id", id as number)
    .single();
  if (error) {
    logger.warn("getRequestFilesById failed:", error.message);
    return null;
  }
  if (!data) return null;
  return {
    docs: (data.docs as unknown[] | null) ?? null,
    invoices: (data.invoices as unknown[] | null) ?? null,
  };
}

// The highest non-draft ref_no for a year (e.g. "2026-00042") — the RLS-scoped
// client-side fallback when the next_ref_no RPC is unavailable. Null if none.
export async function getMaxRefNoForYear(year: string | number): Promise<string | null> {
  const c = getTypedClient();
  if (!c) return null;
  const { data, error } = await c
    .from("requests")
    .select("ref_no")
    .like("ref_no", `${year}-%`)
    .not("ref_no", "like", "DRAFT%")
    .order("ref_no", { ascending: false })
    .limit(1);
  if (error) {
    logger.warn("getMaxRefNoForYear failed:", error.message);
    return null;
  }
  return data && data.length > 0 ? ((data[0].ref_no as string | null) ?? null) : null;
}

// How many requests reference a delivery address (admin "address in use?" warning
// before a soft-delete). 0 on error / no client.
export async function countRequestsByDeliveryAddressId(deliveryAddressId: string): Promise<number> {
  const c = getTypedClient();
  if (!c) return 0;
  const { count, error } = await c
    .from("requests")
    .select("id", { count: "exact", head: true })
    .eq("delivery_address_id", deliveryAddressId);
  if (error) {
    logger.warn("countRequestsByDeliveryAddressId failed:", error.message);
    return 0;
  }
  return count ?? 0;
}

// How many in-flight (Submitted / Part Processed) requests a shelter has — shown
// in the shelter-deactivate confirm so the admin knows what's still open (they
// are NOT cancelled; office staff can still process them). Keyed by shelter_id
// (the surrogate id; identity Phase 2). 0 on error / no client.
export async function countInflightRequestsByShelterId(shelterId: string): Promise<number> {
  const c = getTypedClient();
  if (!c) return 0;
  const { count, error } = await c
    .from("requests")
    .select("id", { count: "exact", head: true })
    .eq("shelter_id", shelterId)
    .in("status", [...ACTIVE_REQUEST_STATUSES]);
  if (error) {
    logger.warn("countInflightRequestsByShelterId failed:", error.message);
    return 0;
  }
  return count ?? 0;
}

// Fresh `notes` array for a request — the concurrency-safe re-read components do
// before appending a comment / approval summary (so a concurrent writer's notes
// aren't clobbered). Returns null on error / no client / not found.
export async function getRequestNotesById(
  id: string | number | undefined
): Promise<unknown[] | null> {
  const c = getTypedClient();
  if (!c) return null;
  const { data, error } = await c
    .from("requests")
    .select("notes")
    .eq("id", id as number)
    .single();
  if (error) {
    logger.warn("getRequestNotesById failed:", error.message);
    return null;
  }
  return ((data?.notes as unknown[] | null) ?? null) as unknown[] | null;
}

// Atomically append one or more notes to requests.notes via the append_request_notes
// RPC (db-200 / migration …039). The DB concatenates server-side
// (notes = coalesce(notes,'[]') || p_notes), so a stale browser copy of the array can
// no longer clobber another actor's note — the data-loss class documented in
// docs/notes-atomic-append-plan.md. Returns the FULL, current notes array after the
// append, so callers should setNotes(result.notes) to show everyone's latest (including
// notes appended concurrently), not just their own optimistic entry.
//   notesToAppend — an ARRAY of note objects (the append is atomic for the whole array).
//   opts.updatedBy — display name written to requests.updated_by (display-only).
//   opts.markUnread — true for a STAFF comment (sets shelter_has_unread_comment); false
//                     for a shelter comment or a silent note. (updated_by_role is derived
//                     server-side, so it is NOT a parameter.)
export async function appendRequestNotes(
  requestId: string | number | null | undefined,
  notesToAppend: unknown[],
  opts?: { updatedBy?: string | null; markUnread?: boolean }
): Promise<{ notes: unknown[] | null; error: { message: string } | null }> {
  const c = getTypedClient();
  if (!c) return { notes: null, error: null };
  try {
    const { data, error } = await c.rpc("append_request_notes", {
      p_request_id: requestId as number,
      p_notes: (notesToAppend ?? []) as Json,
      p_updated_by: opts?.updatedBy ?? "",
      p_mark_unread: opts?.markUnread ?? false,
    });
    if (error) {
      logger.warn("append_request_notes failed:", error.message);
      return { notes: null, error };
    }
    return { notes: (data as unknown as unknown[] | null) ?? null, error: null };
  } catch (e) {
    logger.warn("append_request_notes error:", e);
    return { notes: null, error: { message: String(e) } };
  }
}

// Persist a request's children via the save_request_children RPC.
export async function saveRequestChildren(
  requestId: string | number,
  children: unknown
): Promise<WriteResult> {
  const c = getTypedClient();
  if (!c) return { error: null };
  try {
    const { error } = await c.rpc("save_request_children", {
      p_request_id: requestId as number,
      p_children: (children ?? []) as Json,
    });
    if (error) logger.warn("save_request_children failed:", error.message);
    return { error };
  } catch (e) {
    logger.warn("save_request_children error:", e);
    return { error: { message: String(e) } };
  }
}

// The child ids (uuids) for a request — used by the OSD realtime watch to scope
// its subscription to this request's children. Empty array on error / no client.
export async function listChildIdsByRequestId(
  requestId: number | string | null | undefined
): Promise<string[]> {
  const c = getTypedClient();
  if (!c) return [];
  const { data, error } = await c
    .from("request_children")
    .select("id")
    .eq("request_id", requestId as number);
  if (error) {
    logger.warn("listChildIdsByRequestId failed:", error.message);
    return [];
  }
  return (data ?? []).map((r) => r.id);
}

// ── RPC wrappers (request-scoped) ────────────────────────────────────────────
// Thin typed wrappers over the request Postgres RPCs so call sites import a named
// function instead of a stringly-typed .rpc("…"). Each returns the raw
// { data, error } the callers already branch on (ref_no fallback / submit retry
// live at the call site), logs on error, and treats a null client as an
// error-free no-op (null data) — same shape the inline calls produced.
type RpcResult<T> = { data: T | null; error: { message: string } | null };

// Server-allocated next ref_no ("2026-00042") via the SECURITY DEFINER function
// (sees ALL requests, bypassing RLS). Callers keep their client-side fallback.
export async function nextRefNo(): Promise<RpcResult<string>> {
  const c = getTypedClient();
  if (!c) return { data: null, error: null };
  try {
    const { data, error } = await c.rpc("next_ref_no");
    if (error) logger.warn("next_ref_no failed:", error.message);
    return { data: (data as string | null) ?? null, error };
  } catch (e) {
    logger.warn("next_ref_no error:", e);
    return { data: null, error: { message: String(e) } };
  }
}

// Preview the NEXT request number ("2026-00211") WITHOUT consuming the sequence —
// for the Config editor. Null on error / no client. (db-107)
export async function getNextRefNoPreview(): Promise<string | null> {
  const c = getTypedClient();
  if (!c) return null;
  const { data, error } = await c.rpc("get_next_ref_no_preview");
  if (error) {
    logger.warn("get_next_ref_no_preview failed:", error.message);
    return null;
  }
  return (data as string | null) ?? null;
}

// Set the next request number the platform will issue (go-live seeding). The
// numeric part only (the year prefix is applied by the DB from now()). Returns
// the new next ref no on success. SuperUser-only at the DB. (db-107)
export async function setNextRefNo(value: number): Promise<RpcResult<string>> {
  const c = getTypedClient();
  if (!c) return { data: null, error: null };
  try {
    const { data, error } = await c.rpc("set_next_ref_no", { p_value: value });
    if (error) logger.warn("set_next_ref_no failed:", error.message);
    return { data: (data as string | null) ?? null, error };
  } catch (e) {
    logger.warn("set_next_ref_no error:", e);
    return { data: null, error: { message: String(e) } };
  }
}

// Atomic request INSERT/UPDATE + save_request_children in one Postgres
// transaction — the RLS-critical submit path. Returns the requests_view row
// (children included). id = null for a new request. (p_id is typed `number` in
// the generated schema but the DB function defaults it to NULL, so the null is
// bridged with a type-only cast.)
export async function upsertRequestAtomic(
  id: number | string | null,
  request: object,
  children: unknown
): Promise<RpcResult<DbViewRow>> {
  const c = getTypedClient();
  if (!c) return { data: null, error: null };
  try {
    const { data, error } = await c.rpc("upsert_request_atomic", {
      p_id: (id ?? null) as unknown as number,
      p_request: cleanRequestEmails(request) as Json,
      p_children: (children ?? []) as Json,
    });
    if (error) logger.warn("upsert_request_atomic failed:", error.message);
    return { data: (data as unknown as DbViewRow) ?? null, error };
  } catch (e) {
    logger.warn("upsert_request_atomic error:", e);
    return { data: null, error: { message: String(e) } };
  }
}

// This request's children, re-read after an OSD code is assigned so the realtime
// watcher can merge fresh osd_code values into the in-memory request.
export async function getRequestChildren(
  requestId: number | string | null | undefined
): Promise<RpcResult<unknown[]>> {
  const c = getTypedClient();
  if (!c) return { data: null, error: null };
  try {
    const { data, error } = await c.rpc("get_request_children", {
      p_request_id: requestId as number,
    });
    if (error) logger.warn("get_request_children failed:", error.message);
    return { data: (data as unknown as unknown[] | null) ?? null, error };
  } catch (e) {
    logger.warn("get_request_children error:", e);
    return { data: null, error: { message: String(e) } };
  }
}

// Append one entry to a request's docs[] via the concurrency-safe RPC and return
// the updated docs array. (request_id is typed `string` in the generated schema;
// the caller's loose id is bridged with a type-only cast — runtime value
// unchanged.)
export async function appendToDocs(
  requestId: number | string,
  newDoc: object
): Promise<RpcResult<unknown[]>> {
  const c = getTypedClient();
  if (!c) return { data: null, error: null };
  try {
    const { data, error } = await c.rpc("append_to_docs", {
      request_id: requestId as unknown as string,
      new_doc: newDoc as Json,
    });
    if (error) logger.warn("append_to_docs failed:", error.message);
    return { data: (data as unknown as unknown[] | null) ?? null, error };
  } catch (e) {
    logger.warn("append_to_docs error:", e);
    return { data: null, error: { message: String(e) } };
  }
}
